// ─────────────────────────────────────────────────────────────
// Centralized API Endpoints — hierarchical, namespaced.
//
// Each namespace declares its base ONCE via `mod(base, p => ...)`.
// `root` is derived automatically and every sub-path is built with
// the `p()` helper, so the module prefix is never repeated by hand.
//
// Usage:
//   API.professions.root        => '/professions'
//   API.professions.active      => '/professions/active'
//   API.professions.lookup      => '/professions/lookup'
//   API.professions.byId('x')   => '/professions/x'
//
// IMPORTANT: every value is already a FULL absolute path. Never
// compose them (e.g. joinKeys([API.x.root, API.x.foo])) — that
// duplicates the module segment. Use the constant directly.
// ─────────────────────────────────────────────────────────────

type PathRecord = Record<string, string | ((...args: string[]) => string)>

const mod = <T extends PathRecord = Record<never, never>>(
  base: string,
  build?: (p: (sub: string) => string) => T
): { root: string } & T => {
  const p = (sub: string) => `${base}/${sub}`
  return { root: base, ...(build ? build(p) : ({} as T)) }
}

export const API = {
  // ── Auth ──
  auth: mod('/auth', (p) => ({
    login: p('login'),
    refresh: p('refresh'),
    renewData: p('renew-data'),
    forgotPassword: p('forgot-password'),
    resetPassword: p('reset-password'),
    logout: p('logout'),
    verifyPassword: p('verify-password'),
    changePassword: p('change-password'),
    me: p('me')
  })),

  twoFactor: mod('/2fa', (p) => ({
    authenticate: p('authenticate'),
    generate: p('generate'),
    turnOn: p('turn-on'),
    turnOff: p('turn-off')
  })),

  // ── Users & Roles ──
  users: mod('/users', (p) => ({
    byId: (id: string) => p(id),
    username: p('username'),
    userExist: p('user-exist'),
    lookup: p('lookup'),
    lookupAll: p('lookup-all'),
    emailExist: p('email-exist'),
    advisor: p('advisor'),
    is2faEnabled: p('is-2fa-enabled'),
    twoFaSecret: p('2fa-secret'),
    changePassword: p('change-password'),
    changeUsername: p('change-username'),
    changeEmail: p('change-email'),
    getUsername: p('get-username'),
    getEmail: p('get-email'),
    updateProfile: p('update-profile'),
    searchUsersClients: p('search/users-and-clients')
  })),

  roles: mod('/roles', (p) => ({
    byId: (id: string) => p(id),
    lookup: p('lookup')
  })),

  // ── Companies & Agencies ──
  companies: mod('/companies', (p) => ({
    byId: (id: string) => p(id),
    active: p('active'),
    lookup: p('lookup'),
    idc: p('idc')
  })),

  agencies: mod('/agencies', (p) => ({
    byId: (id: string) => p(id),
    active: p('active'),
    lookup: p('lookup')
  })),

  // ── Societies, Projects, Properties ──
  societies: mod('/societies', (p) => ({
    byId: (id: string) => p(id),
    active: p('active'),
    lookup: p('lookup')
  })),

  projects: mod('/projects', (p) => ({
    byId: (id: string) => p(id),
    active: p('active'),
    lookup: p('lookup'),
    printById: (id: string) => p(`print/${id}`)
  })),

  typeProperties: mod('/type-properties', (p) => ({
    byId: (id: string) => p(id),
    printById: (id: string) => p(`print/${id}`),
    active: p('active'),
    lookup: p('lookup'),
    byProject: (projectId: string) => p(`project/${projectId}`)
  })),

  properties: mod('/properties', (p) => ({
    byId: (id: string) => p(id),
    printById: (id: string) => p(`print/${id}`),
    active: p('active'),
    available: p('available'),
    light: p('light')
  })),

  // ── Representatives & Mandataries ──
  representatives: mod('/representatives', (p) => ({
    byId: (id: string) => p(id),
    lookup: p('lookup'),
    activeBySociety: (societyId: string) => p(`${societyId}/active`),
    printById: (id: string) => p(`print/${id}`),
    printActiveBySociety: (societyId: string) => p(`print/${societyId}/active`)
  })),

  mandataries: mod('/mandataries', (p) => ({
    byId: (id: string) => p(id),
    active: p('active')
  })),

  // ── Reference data ──
  states: mod('/states', (p) => ({
    byId: (id: string) => p(id),
    lookup: p('lookup')
    // `myTransitions` se retiró el 2026-08-05: lo sucede `GET /api/v2/me/state-transitions`.
  })),

  workflows: mod('/workflows', (p) => ({
    byId: (id: string) => p(id),
    lookup: p('lookup'),
    flowsRenew: p('flows-renew')
  })),

  professions: mod('/professions', (p) => ({
    byId: (id: string) => p(id),
    active: p('active'),
    lookup: p('lookup'),
    print: p('print')
  })),

  countries: mod('/countries', (p) => ({
    byId: (id: string) => p(id)
  })),

  departments: mod('/departments'),

  municipalities: mod('/municipalities'),

  currencies: mod('/currencies'),

  products: mod('/products', (p) => ({
    byId: (id: string) => p(id),
    active: p('active')
  })),

  subproducts: mod('/subproducts', (p) => ({
    byId: (id: string) => p(id),
    active: p('active')
  })),

  // `groups` vivió acá hasta el 2026-08-11 y se retiró con la decisión Nº 31: el namespace
  // NO EXISTE en ninguna versión de la API —medido contra la tabla de rutas viva del
  // servidor, 467 rutas, `GET /api/v1/groups/active` → 404—, así que no era un cableado
  // mal hecho sino una capacidad ausente. Su único consumidor era el modal de alta de
  // cliente, que se tragaba el 404 y dejaba el desplegable vacío sin avisar.
  catalogs: mod('/catalogs'),

  // ── Clients ──
  // ☠️ CINCO constantes muertas se retiraron el 2026-08-12 (decisión Nº 25), repartidas en cuatro
  // namespaces: `clients.modifyGroup`, `clients.countByState`, `deliveries.completed`,
  // `reports.accountProduct` y `sap.setDependenciesOp`. Ninguna nombraba una ruta que exista en
  // ningún controller y ninguna tenía llamador: eran cinco 404 esperando a que alguien las usara.
  // `negotiations-v1-paths.spec.ts` las venía nombrando UNA POR UNA desde el 2026-08-05 —«no se
  // borran acá porque son de otros dominios»—, así que el guardarraíl hizo su trabajo y el papel
  // esperó una semana a que alguien decidiera podarlas.
  clients: mod('/clients', (p) => ({
    byId: (id: string) => p(id),
    printById: (id: string) => p(`${id}/print`),
    existIdentification: p('exist-identification'),
    nextState: (id: string) => p(`${id}/next-state`),
    reject: (id: string) => p(`${id}/reject`),
    inactivate: (id: string) => p(`${id}/inactivate`),
    activate: (id: string) => p(`${id}/activate`),
    renew: (id: string) => p(`${id}/renew`),
    modifyAdvisor: p('modify/advisor'),
    selections: p('selections')
  })),

  clientsJuridical: mod('/clients-juridical'),

  clientConfig: mod('/client-config'),

  // ── Negotiations ──
  negotiations: mod('/negotiations', (p) => ({
    // `legacy` y `metrics` se fueron el 2026-08-05 con sus rutas: el listado v1 no tenía
    // llamadores y `GET /negotiations/metrics` ya se había retirado del servidor, así que esta
    // constante apuntaba a un 404 que nadie iba a encontrar hasta pedirlo.
    byId: (id: string) => p(id),
    // Las CINCO transiciones —`inactive`, `active`, `next-state`, `rescind`, `reject`— se
    // retiraron del servidor con la tajada 8 y viajan por `POST /api/v2/admin/negotiations/:id/…`.
    // Sus constantes sobrevivieron sin llamadores hasta el 2026-08-05, apuntando a cinco 404 que
    // nadie iba a encontrar salvo llamándolos. `historyStates` se fue por lo mismo: el historial
    // lo da `GET /api/v2/admin/negotiations/:id/history`.
    //
    // Lo vigila `negotiations-v1-paths.spec.ts`: una constante que nombra una ruta que el
    // controller de v1 ya no declara es un 404 esperando a que alguien la use.
    sapPayment: (id: string) => p(`${id}/sap-payment`),
    sapProductionOrder: (id: string) => p(`${id}/sap-production-order`),
    approvedWithDelivery: p('approved-with-delivery')
  })),

  // ── Deliveries ──
  deliveries: mod('/deliveries', (p) => ({
    byId: (id: string) => p(id),
    pending: p('pending'),
    byNegotiation: (negotiationId: string) => p(`by-negotiation/${negotiationId}`),
    status: (id: string) => p(`${id}/status`)
  })),

  // ── Availability & Reservations ──
  availability: mod('/availability', (p) => ({
    byId: (id: string) => p(id),
    metrics: p('metrics'),
    monthlyActivity: p('monthly-activity')
  })),

  inventoryComments: mod('/inventory-comments', (p) => ({
    byProperty: (propertyId: string) => p(`property/${propertyId}`),
    byId: (commentId: string) => p(commentId)
  })),

  reservations: mod('/reservations', (p) => ({
    byId: (id: string) => p(id),
    release: (id: string) => p(`${id}/release`),
    authorizeById: (id: string) => p(`${id}/authorize`),
    declineById: (id: string) => p(`${id}/decline`),
    resendAuthorization: (id: string) => p(`${id}/resend-authorization`),
    authorizeByToken: (token: string) => p(`authorize/${token}`),
    declineByToken: (token: string) => p(`decline/${token}`),
    byProperty: (propertyId: string) => p(`property/${propertyId}`),
    history: (propertyId: string) => p(`property/${propertyId}/history`)
  })),

  // ── Files & Reports ──
  folders: mod('/folders', (p) => ({
    folderExist: p('folder-exist'),
    client: (id: string) => p(id),
    negotiation: (id: string) => p(`${id}/negotiation`)
  })),

  attachments: mod('/attachments', (p) => ({
    byId: (id: string) => p(id),
    url: p('url'),
    uploadImage: p('upload-image'),
    client: (id: string) => p(id),
    negotiation: (id: string) => p(`${id}/negotiation`),
    download: (id: string) => p(`${id}/url`)
  })),

  reports: mod('/reports', (p) => ({
    byId: (id: string) => p(id)
  })),

  // ── Integrations ──
  sap: mod('/sap', (p) => ({
    opDependencies: p('op-dependencies'),
    opLevel: p('op-levels'),
    setOrder: p('orders'),
    getBaseDataOrderProduction: p('production-order-base'),
    getBaseDataOrderProductionByProperty: p('production-order-base/by-property')
  })),

  renap: mod('/renap', (p) => ({
    search: p('search')
  })),

  // ── Approval Flows ──
  approvalFlows: mod('/document-approval-flows', (p) => ({
    byId: (id: string) => p(id)
  })),

  // ── Client Portal (formularios autogestionados) ──
  portalForms: mod('/client-portal', (p) => ({
    public: p('public'),
    quickClients: p('quick-clients'),
    quickClientsVerify: p('quick-clients/verify'),
    markClientVerified: (clientId: string) => p(`clients/${clientId}/mark-verified`)
  })),

  // ── Audit (bitácora central de la plataforma) ──
  audit: mod('/audit'),

  // ── Flows (motor de flujos: definiciones que dirigen las secciones del portal) ──
  flows: mod('/flows', (p) => ({
    byId: (id: string) => p(id)
  })),

  // ── Templates (documentos del editor, entidad de primera clase) ──
  templates: mod('/templates', (p) => ({
    byId: (id: string) => p(id),
    printById: (id: string) => p(`print/${id}`),
    applyPageConfig: p('apply-page-config')
  }))
} as const
